/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
import { regMatchAll, maxDotNumber, clamp } from "@zyzgroup/core-common";
import { toDegree, toRadian } from "./math";
import Point3D from "./shapes/Point3D";
import { Vector2, Vector3 } from "./Vector";
import { Euler } from "./Euler";
import { Quaternion } from "./Quaternion";

export const WebGLCoordinateSystem = 2000;
export const WebGPUCoordinateSystem = 2001;

/// 按列顺序 - 4
export type TMatrix2Value = [number, number, number, number];
const DEFAULT_MATRIX2_VALUE: TMatrix2Value = [1, 0, 0, 1];

/// 按列顺序 - 6
export type TMatrixValue = [number, number, number, number, number, number];
const DEFAULT_MATRIX_VALUE: TMatrixValue = [1, 0, 0, 1, 0, 0];

/// 按列顺序 - 9
export type TMatrix3Value = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];
const DEFAULT_MATRIX3_VALUE: TMatrix3Value = [1, 0, 0, 0, 1, 0, 0, 0, 1];

/// 按列顺序 - 16
export type TMatrix4Value = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number
];
const DEFAULT_MATRIX4_VALUE: TMatrix4Value = [
  1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1
];

export class Matrix2 {
  /// 按列顺序
  value: TMatrix2Value;

  constructor(value: TMatrix2Value = DEFAULT_MATRIX2_VALUE) {
    this.value = Array.from(value) as TMatrix2Value;
  }

  get m11() {
    return this.value[0];
  }
  get m12() {
    return this.value[1];
  }
  get m21() {
    return this.value[2];
  }
  get m22() {
    return this.value[3];
  }

  set m11(v: number) {
    this.value[0] = v;
  }
  set m12(v: number) {
    this.value[1] = v;
  }
  set m21(v: number) {
    this.value[2] = v;
  }
  set m22(v: number) {
    this.value[3] = v;
  }

  /// 按行顺序
  set(n11 = 1, n12 = 0, n21 = 0, n22 = 1) {
    this.m11 = n11;
    this.m21 = n12;
    this.m12 = n21;
    this.m22 = n22;
    return this;
  }

  identity() {
    this.value = Array.from(DEFAULT_MATRIX2_VALUE) as TMatrix2Value;
    return this;
  }

  get isIdentity(): boolean {
    return this.m11 == 1 && this.m12 == 0 && this.m21 == 0 && this.m22 == 1;
  }

  copy(m: Matrix2) {
    this.m11 = m.m11;
    this.m12 = m.m12;
    this.m21 = m.m21;
    this.m22 = m.m22;
    return this;
  }

  clone(): Matrix2 {
    return new Matrix2(this.value);
  }

  fromArray(array: number[], offset = 0) {
    for (let i = 0; i < 4; i++) {
      this.value[i] = array[i + offset];
    }
    return this;
  }

  toArray(array: number[] = [], offset = 0) {
    array[offset] = this.m11;
    array[offset + 1] = this.m12;
    array[offset + 2] = this.m21;
    array[offset + 3] = this.m22;
    return array;
  }

  multiplyMatrices(a: Matrix2, b: Matrix2) {
    const ae = a.value;
    const be = b.value;
    const te = this.value;

    const a11 = ae[0];
    const a12 = ae[2];
    const a21 = ae[1];
    const a22 = ae[3];

    const b11 = be[0];
    const b12 = be[2];
    const b21 = be[1];
    const b22 = be[3];

    te[0] = a11 * b11 + a12 * b21;
    te[2] = a11 * b12 + a12 * b22;

    te[1] = a21 * b11 + a22 * b21;
    te[3] = a21 * b12 + a22 * b22;

    return this;
  }

  multiply(m: Matrix2) {
    return this.multiplyMatrices(this, m);
  }

  premultiply(m: Matrix2) {
    return this.multiplyMatrices(m, this);
  }

  multiplyScalar(s: number) {
    this.m11 *= s;
    this.m12 *= s;
    this.m21 *= s;
    this.m22 *= s;
    return this;
  }

  /// 行列式
  determinant(): number {
    return this.m11 * this.m22 - this.m21 * this.m12;
  }
}

export class Matrix3 {
  /// 按列顺序
  value: TMatrix3Value;

  constructor(value: TMatrix3Value = DEFAULT_MATRIX3_VALUE) {
    this.value = Array.from(value) as TMatrix3Value;
  }

  get m11() {
    return this.value[0];
  }
  get m12() {
    return this.value[1];
  }
  get m13() {
    return this.value[2];
  }
  get m21() {
    return this.value[3];
  }
  get m22() {
    return this.value[4];
  }
  get m23() {
    return this.value[5];
  }
  get m31() {
    return this.value[6];
  }
  get m32() {
    return this.value[7];
  }
  get m33() {
    return this.value[8];
  }

  set m11(v: number) {
    this.value[0] = v;
  }
  set m12(v: number) {
    this.value[1] = v;
  }
  set m13(v: number) {
    this.value[2] = v;
  }
  set m21(v: number) {
    this.value[3] = v;
  }
  set m22(v: number) {
    this.value[4] = v;
  }
  set m23(v: number) {
    this.value[5] = v;
  }
  set m31(v: number) {
    this.value[6] = v;
  }
  set m32(v: number) {
    this.value[7] = v;
  }
  set m33(v: number) {
    this.value[8] = v;
  }

  /// 按行顺序
  set(
    n11 = 1,
    n12 = 0,
    n13 = 0,
    n21 = 0,
    n22 = 1,
    n23 = 0,
    n31 = 0,
    n32 = 0,
    n33 = 1
  ) {
    const te = this.value;
    te[0] = n11;
    te[1] = n21;
    te[2] = n31;
    te[3] = n12;
    te[4] = n22;
    te[5] = n32;
    te[6] = n13;
    te[7] = n23;
    te[8] = n33;
    return this;
  }

  identity() {
    this.value = Array.from(DEFAULT_MATRIX3_VALUE) as TMatrix3Value;
    return this;
  }

  get isIdentity(): boolean {
    return (
      this.m11 == 1 &&
      this.m12 == 0 &&
      this.m13 == 0 &&
      this.m21 == 0 &&
      this.m22 == 1 &&
      this.m23 == 0 &&
      this.m31 == 0 &&
      this.m32 == 0 &&
      this.m33 == 1
    );
  }

  copy(m: Matrix3) {
    this.m11 = m.m11;
    this.m12 = m.m12;
    this.m13 = m.m13;
    this.m21 = m.m21;
    this.m22 = m.m22;
    this.m23 = m.m23;
    this.m31 = m.m31;
    this.m32 = m.m32;
    this.m33 = m.m33;
    return this;
  }

  clone(): Matrix3 {
    return new Matrix3(this.value);
  }

  fromArray(array: number[], offset = 0) {
    for (let i = 0; i < 9; i++) {
      this.value[i] = array[i + offset];
    }
    return this;
  }

  toArray(array: number[] = [], offset = 0) {
    array[offset] = this.m11;
    array[offset + 1] = this.m12;
    array[offset + 2] = this.m13;
    array[offset + 3] = this.m21;
    array[offset + 4] = this.m22;
    array[offset + 5] = this.m23;
    array[offset + 6] = this.m31;
    array[offset + 7] = this.m32;
    array[offset + 8] = this.m33;
    return array;
  }

  extractBasis(xAxis: Vector3, yAxis: Vector3, zAxis: Vector3) {
    xAxis.setFromMatrix3Column(this, 0);
    yAxis.setFromMatrix3Column(this, 1);
    zAxis.setFromMatrix3Column(this, 2);
    return this;
  }

  setFromMatrix4(m: Matrix4) {
    this.set(m.m11, m.m21, m.m31, m.m12, m.m22, m.m32, m.m13, m.m23, m.m33);
    return this;
  }

  multiplyMatrices(a: Matrix3, b: Matrix3) {
    const ae = a.value;
    const be = b.value;
    const te = this.value;

    const a11 = ae[0];
    const a12 = ae[3];
    const a13 = ae[6];
    const a21 = ae[1];
    const a22 = ae[4];
    const a23 = ae[7];
    const a31 = ae[2];
    const a32 = ae[5];
    const a33 = ae[8];

    const b11 = be[0];
    const b12 = be[3];
    const b13 = be[6];
    const b21 = be[1];
    const b22 = be[4];
    const b23 = be[7];
    const b31 = be[2];
    const b32 = be[5];
    const b33 = be[8];

    te[0] = a11 * b11 + a12 * b21 + a13 * b31;
    te[3] = a11 * b12 + a12 * b22 + a13 * b32;
    te[6] = a11 * b13 + a12 * b23 + a13 * b33;

    te[1] = a21 * b11 + a22 * b21 + a23 * b31;
    te[4] = a21 * b12 + a22 * b22 + a23 * b32;
    te[7] = a21 * b13 + a22 * b23 + a23 * b33;

    te[2] = a31 * b11 + a32 * b21 + a33 * b31;
    te[5] = a31 * b12 + a32 * b22 + a33 * b32;
    te[8] = a31 * b13 + a32 * b23 + a33 * b33;

    return this;
  }

  multiply(m: Matrix3) {
    return this.multiplyMatrices(this, m);
  }

  premultiply(m: Matrix3) {
    return this.multiplyMatrices(m, this);
  }

  multiplyScalar(s: number) {
    this.m11 *= s;
    this.m12 *= s;
    this.m13 *= s;
    this.m21 *= s;
    this.m22 *= s;
    this.m23 *= s;
    this.m31 *= s;
    this.m32 *= s;
    this.m33 *= s;
    return this;
  }

  /// 行列式
  determinant(): number {
    const te = this.value;
    const a = te[0];
    const b = te[1];
    const c = te[2];
    const d = te[3];
    const e = te[4];
    const f = te[5];
    const g = te[6];
    const h = te[7];
    const i = te[8];
    return (
      a * e * i - a * f * h - b * d * i + b * f * g + c * d * h - c * e * g
    );
  }

  inverse() {
    const te = this.value;
    const n11 = te[0];
    const n21 = te[1];
    const n31 = te[2];
    const n12 = te[3];
    const n22 = te[4];
    const n32 = te[5];
    const n13 = te[6];
    const n23 = te[7];
    const n33 = te[8];
    const t11 = n33 * n22 - n32 * n23;
    const t12 = n32 * n13 - n33 * n12;
    const t13 = n23 * n12 - n22 * n13;
    const det = n11 * t11 + n21 * t12 + n31 * t13;

    if (det === 0) {
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0);
    }

    const detInv = 1 / det;

    te[0] = t11 * detInv;
    te[1] = (n31 * n23 - n33 * n21) * detInv;
    te[2] = (n32 * n21 - n31 * n22) * detInv;

    te[3] = t12 * detInv;
    te[4] = (n33 * n11 - n31 * n13) * detInv;
    te[5] = (n31 * n12 - n32 * n11) * detInv;

    te[6] = t13 * detInv;
    te[7] = (n21 * n13 - n23 * n11) * detInv;
    te[8] = (n22 * n11 - n21 * n12) * detInv;

    return this;
  }

  transpose() {
    let tmp;
    const m = this.value;

    tmp = m[1];
    m[1] = m[3];
    m[3] = tmp;
    tmp = m[2];
    m[2] = m[6];
    m[6] = tmp;
    tmp = m[5];
    m[5] = m[7];
    m[7] = tmp;

    return this;
  }

  getNormalMatrix(matrix4: Matrix4) {
    return this.setFromMatrix4(matrix4).inverse().transpose();
  }

  transposeIntoArray(r: number[]) {
    const m = this.value;
    r[0] = m[0];
    r[1] = m[3];
    r[2] = m[6];
    r[3] = m[1];
    r[4] = m[4];
    r[5] = m[7];
    r[6] = m[2];
    r[7] = m[5];
    r[8] = m[8];
    return this;
  }

  setUvTransform(
    tx: number,
    ty: number,
    sx: number,
    sy: number,
    rotation: number,
    cx: number,
    cy: number
  ) {
    const c = Math.cos(rotation);
    const s = Math.sin(rotation);
    this.set(
      sx * c,
      sx * s,
      -sx * (c * cx + s * cy) + cx + tx,
      -sy * s,
      sy * c,
      -sy * (-s * cx + c * cy) + cy + ty,
      0,
      0,
      1
    );
    return this;
  }

  scale(sx: number, sy: number) {
    this.premultiply(_m3.makeScale(sx, sy));
    return this;
  }

  rotate(theta: number) {
    this.premultiply(_m3.makeRotation(-theta));
    return this;
  }

  translate(tx: number, ty: number) {
    this.premultiply(_m3.makeTranslation(tx, ty));
    return this;
  }

  makeTranslation(x: Vector2 | number, y?: number) {
    if (typeof x === "number") {
      this.set(1, 0, x, 0, 1, y, 0, 0, 1);
    } else {
      this.set(1, 0, x.x, 0, 1, x.y, 0, 0, 1);
    }
    return this;
  }

  // counterclockwise
  makeRotation(theta: number) {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    this.set(c, -s, 0, s, c, 0, 0, 0, 1);
    return this;
  }

  makeScale(x: number, y: number) {
    this.set(x, 0, 0, 0, y, 0, 0, 0, 1);
    return this;
  }

  equals(matrix: Matrix3) {
    for (let i = 0; i < 9; i++) {
      if (this.value[i] !== matrix.value[i]) {
        return false;
      }
    }
    return true;
  }
}

export class Matrix4 {
  /// 按列顺序
  value: TMatrix4Value;
  is3dSpecial = false;

  constructor(value: TMatrix4Value = DEFAULT_MATRIX4_VALUE) {
    this.value = Array.from(value) as TMatrix4Value;
  }

  get m11() {
    return this.value[0];
  }
  get m12() {
    return this.value[1];
  }
  get m13() {
    return this.value[2];
  }
  get m14() {
    return this.value[3];
  }
  get m21() {
    return this.value[4];
  }
  get m22() {
    return this.value[5];
  }
  get m23() {
    return this.value[6];
  }
  get m24() {
    return this.value[7];
  }
  get m31() {
    return this.value[8];
  }
  get m32() {
    return this.value[9];
  }
  get m33() {
    return this.value[10];
  }
  get m34() {
    return this.value[11];
  }
  get m41() {
    return this.value[12];
  }
  get m42() {
    return this.value[13];
  }
  get m43() {
    return this.value[14];
  }
  get m44() {
    return this.value[15];
  }

  set m11(v: number) {
    this.value[0] = v;
  }
  set m12(v: number) {
    this.value[1] = v;
  }
  set m13(v: number) {
    this.value[2] = v;
  }
  set m14(v: number) {
    this.value[3] = v;
  }
  set m21(v: number) {
    this.value[4] = v;
  }
  set m22(v: number) {
    this.value[5] = v;
  }
  set m23(v: number) {
    this.value[6] = v;
  }
  set m24(v: number) {
    this.value[7] = v;
  }
  set m31(v: number) {
    this.value[8] = v;
  }
  set m32(v: number) {
    this.value[9] = v;
  }
  set m33(v: number) {
    this.value[10] = v;
  }
  set m34(v: number) {
    this.value[11] = v;
  }
  set m41(v: number) {
    this.value[12] = v;
  }
  set m42(v: number) {
    this.value[13] = v;
  }
  set m43(v: number) {
    this.value[14] = v;
  }
  set m44(v: number) {
    this.value[15] = v;
  }

  /// 按行顺序
  set(
    n11 = 1,
    n12 = 0,
    n13 = 0,
    n14 = 0,
    n21 = 0,
    n22 = 1,
    n23 = 0,
    n24 = 0,
    n31 = 0,
    n32 = 0,
    n33 = 1,
    n34 = 0,
    n41 = 0,
    n42 = 0,
    n43 = 0,
    n44 = 1
  ) {
    const te = this.value;
    te[0] = n11;
    te[4] = n12;
    te[8] = n13;
    te[12] = n14;
    te[1] = n21;
    te[5] = n22;
    te[9] = n23;
    te[13] = n24;
    te[2] = n31;
    te[6] = n32;
    te[10] = n33;
    te[14] = n34;
    te[3] = n41;
    te[7] = n42;
    te[11] = n43;
    te[15] = n44;
    return this;
  }

  is2D(): boolean {
    return (
      !this.is3dSpecial &&
      this.m31 == 0 &&
      this.m32 == 0 &&
      this.m33 == 1 &&
      this.m34 == 0 &&
      this.m43 == 0 &&
      this.m44 == 1
    );
  }

  is3D(): boolean {
    return !this.is2D();
  }

  identity() {
    this.value = Array.from(DEFAULT_MATRIX4_VALUE) as TMatrix4Value;
    return this;
  }

  get isIdentity(): boolean {
    return (
      this.m11 == 1 &&
      this.m12 == 0 &&
      this.m13 == 0 &&
      this.m14 == 0 &&
      this.m21 == 0 &&
      this.m22 == 1 &&
      this.m23 == 0 &&
      this.m24 == 0 &&
      this.m31 == 0 &&
      this.m32 == 0 &&
      this.m33 == 1 &&
      this.m34 == 0 &&
      this.m41 == 0 &&
      this.m42 == 0 &&
      this.m43 == 0 &&
      this.m44 == 1
    );
  }

  copy(m: Matrix4) {
    this.m11 = m.m11;
    this.m12 = m.m12;
    this.m13 = m.m13;
    this.m14 = m.m14;
    this.m21 = m.m21;
    this.m22 = m.m22;
    this.m23 = m.m23;
    this.m24 = m.m24;
    this.m31 = m.m31;
    this.m32 = m.m32;
    this.m33 = m.m33;
    this.m34 = m.m34;
    this.m41 = m.m41;
    this.m42 = m.m42;
    this.m43 = m.m43;
    this.m44 = m.m44;
    return this;
  }

  clone(): Matrix4 {
    return new Matrix4(this.value);
  }

  // matrix4(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1)
  static fromString(s: string) {
    const m = new Matrix4();
    const results = regMatchAll("number", s);
    if (results.length === 16) {
      results.forEach((result, index) => {
        if (result.fullMatch) {
          m.value[index] = parseFloat(result.fullMatch);
        }
      });
    }
    return m;
  }

  fromArray(array: number[], offset = 0) {
    for (let i = 0; i < 16; i++) {
      this.value[i] = array[i + offset];
    }
    return this;
  }

  toArray(array: number[] = [], offset = 0) {
    const te = this.value;

    array[offset] = te[0];
    array[offset + 1] = te[1];
    array[offset + 2] = te[2];
    array[offset + 3] = te[3];

    array[offset + 4] = te[4];
    array[offset + 5] = te[5];
    array[offset + 6] = te[6];
    array[offset + 7] = te[7];

    array[offset + 8] = te[8];
    array[offset + 9] = te[9];
    array[offset + 10] = te[10];
    array[offset + 11] = te[11];

    array[offset + 12] = te[12];
    array[offset + 13] = te[13];
    array[offset + 14] = te[14];
    array[offset + 15] = te[15];

    return array;
  }

  copyPosition(m: Matrix4) {
    const te = this.value;
    const me = m.value;
    te[12] = me[12];
    te[13] = me[13];
    te[14] = me[14];
    return this;
  }

  setFromMatrix3(m: Matrix3) {
    const me = m.value;
    this.set(
      me[0],
      me[3],
      me[6],
      0,
      me[1],
      me[4],
      me[7],
      0,
      me[2],
      me[5],
      me[8],
      0,
      0,
      0,
      0,
      1
    );
    return this;
  }

  extractBasis(xAxis: Vector3, yAxis: Vector3, zAxis: Vector3) {
    xAxis.setFromMatrix4Column(this, 0);
    yAxis.setFromMatrix4Column(this, 1);
    zAxis.setFromMatrix4Column(this, 2);
    return this;
  }

  makeBasis(xAxis: Vector3, yAxis: Vector3, zAxis: Vector3) {
    this.set(
      xAxis.x,
      yAxis.x,
      zAxis.x,
      0,
      xAxis.y,
      yAxis.y,
      zAxis.y,
      0,
      xAxis.z,
      yAxis.z,
      zAxis.z,
      0,
      0,
      0,
      0,
      1
    );
    return this;
  }

  normalize() {
    return this.multiplyScalar(1 / (this.m44 || 0.0001));
  }

  // w: perspective
  transformPoint(point: { x: number; y: number; z?: number; w?: number }): {
    x: number;
    y: number;
    z: number;
    w: number;
  } {
    const z = point.z || 1;
    const w = point.w || 1;
    return {
      x: point.x * this.m11 + point.y * this.m21 + z * this.m31 + w * this.m41,
      y: point.x * this.m12 + point.y * this.m22 + z * this.m32 + w * this.m42,
      z: point.x * this.m13 + point.y * this.m23 + z * this.m33 + w * this.m43,
      w: point.x * this.m14 + point.y * this.m24 + z * this.m34 + w * this.m44
    };
  }

  toMatrix(matrix?: Matrix) {
    const { scalarX, scalarY, skewX, skewY, x, y } = this.split();
    const value: TMatrixValue = [scalarX, skewY, skewX, scalarY, x, y];
    if (matrix) {
      matrix.value = value;
      return matrix;
    } else {
      return new Matrix(value);
    }
    // if (!matrix) {
    //   matrix = new Matrix();
    // }
    // matrix.m11 = this.m11;
    // matrix.m12 = this.m12;
    // matrix.m13 = this.m14;
    // matrix.m21 = this.m21;
    // matrix.m22 = this.m22;
    // matrix.m23 = this.m24;
    // matrix.m31 = this.m41;
    // matrix.m32 = this.m42;
    // matrix.m33 = this.m44;
    // return matrix;
  }

  flatten(): Matrix4 {
    this.m31 = 0;
    this.m32 = 0;
    this.m33 = 1;
    this.m34 = 0;
    this.m14 = 0;
    this.m24 = 0;
    this.m43 = 0;
    this.m44 = 1;
    return this;
  }

  toString(fractionalDigits = 6) {
    return `matrix3d(${this.value
      .map((v) => maxDotNumber(v, fractionalDigits))
      .join(",")})`;
  }

  split() {
    const scalarX = Math.sqrt(
      this.m11 * this.m11 + this.m12 * this.m12 + this.m13 * this.m13
    );
    const scalarY = Math.sqrt(
      this.m21 * this.m21 + this.m22 * this.m22 + this.m23 * this.m23
    );
    const scalarZ = Math.sqrt(
      this.m31 * this.m31 + this.m32 * this.m32 + this.m33 * this.m33
    );
    const degreeY = toDegree(clamp(Math.asin(-this.m13), -1, 1));
    let degreeX = toDegree(Math.atan2(this.m23, this.m33));
    if (Math.cos(toRadian(degreeY)) === 0) {
      degreeX = toDegree(Math.atan2(-this.m31, this.m22));
      // degreeZ = 0;
    }
    let degreeZ = toDegree(Math.atan2(this.m12, this.m11));
    if (this.m11 < 0 && this.m22 >= 0) {
      degreeZ += 180;
    }
    return {
      x: this.m41,
      y: this.m42,
      z: this.m43,
      scalarX,
      scalarY,
      scalarZ,
      degreeX,
      degreeY,
      degreeZ,
      skewX: toDegree(this.m14),
      skewY: toDegree(this.m24),
      skewZ: toDegree(this.m34),
      skewW: this.m44,
      perspective: this.m34 ? -(1 / this.m34) : 0
    };
  }

  extractRotation(m: Matrix4) {
    // this method does not support reflection matrices
    const te = this.value;
    const me = m.value;

    const scaleX = 1 / _v3.setFromMatrix4Column(m, 0).length();
    const scaleY = 1 / _v3.setFromMatrix4Column(m, 1).length();
    const scaleZ = 1 / _v3.setFromMatrix4Column(m, 2).length();

    te[0] = me[0] * scaleX;
    te[1] = me[1] * scaleX;
    te[2] = me[2] * scaleX;
    te[3] = 0;

    te[4] = me[4] * scaleY;
    te[5] = me[5] * scaleY;
    te[6] = me[6] * scaleY;
    te[7] = 0;

    te[8] = me[8] * scaleZ;
    te[9] = me[9] * scaleZ;
    te[10] = me[10] * scaleZ;
    te[11] = 0;

    te[12] = 0;
    te[13] = 0;
    te[14] = 0;
    te[15] = 1;

    return this;
  }

  makeRotationFromEuler(euler: Euler) {
    const te = this.value;

    const x = euler.x;
    const y = euler.y;
    const z = euler.z;
    const a = Math.cos(x);
    const b = Math.sin(x);
    const c = Math.cos(y);
    const d = Math.sin(y);
    const e = Math.cos(z);
    const f = Math.sin(z);

    if (euler.order === "XYZ") {
      const ae = a * e;
      const af = a * f;
      const be = b * e;
      const bf = b * f;

      te[0] = c * e;
      te[4] = -c * f;
      te[8] = d;

      te[1] = af + be * d;
      te[5] = ae - bf * d;
      te[9] = -b * c;

      te[2] = bf - ae * d;
      te[6] = be + af * d;
      te[10] = a * c;
    } else if (euler.order === "YXZ") {
      const ce = c * e;
      const cf = c * f;
      const de = d * e;
      const df = d * f;

      te[0] = ce + df * b;
      te[4] = de * b - cf;
      te[8] = a * d;

      te[1] = a * f;
      te[5] = a * e;
      te[9] = -b;

      te[2] = cf * b - de;
      te[6] = df + ce * b;
      te[10] = a * c;
    } else if (euler.order === "ZXY") {
      const ce = c * e;
      const cf = c * f;
      const de = d * e;
      const df = d * f;

      te[0] = ce - df * b;
      te[4] = -a * f;
      te[8] = de + cf * b;

      te[1] = cf + de * b;
      te[5] = a * e;
      te[9] = df - ce * b;

      te[2] = -a * d;
      te[6] = b;
      te[10] = a * c;
    } else if (euler.order === "ZYX") {
      const ae = a * e;
      const af = a * f;
      const be = b * e;
      const bf = b * f;

      te[0] = c * e;
      te[4] = be * d - af;
      te[8] = ae * d + bf;

      te[1] = c * f;
      te[5] = bf * d + ae;
      te[9] = af * d - be;

      te[2] = -d;
      te[6] = b * c;
      te[10] = a * c;
    } else if (euler.order === "YZX") {
      const ac = a * c;
      const ad = a * d;
      const bc = b * c;
      const bd = b * d;

      te[0] = c * e;
      te[4] = bd - ac * f;
      te[8] = bc * f + ad;

      te[1] = f;
      te[5] = a * e;
      te[9] = -b * e;

      te[2] = -d * e;
      te[6] = ad * f + bc;
      te[10] = ac - bd * f;
    } else if (euler.order === "XZY") {
      const ac = a * c;
      const ad = a * d;
      const bc = b * c;
      const bd = b * d;

      te[0] = c * e;
      te[4] = -f;
      te[8] = d * e;

      te[1] = ac * f + bd;
      te[5] = a * e;
      te[9] = ad * f - bc;

      te[2] = bc * f - ad;
      te[6] = b * e;
      te[10] = bd * f + ac;
    }

    // bottom row
    te[3] = 0;
    te[7] = 0;
    te[11] = 0;

    // last column
    te[12] = 0;
    te[13] = 0;
    te[14] = 0;
    te[15] = 1;

    return this;
  }

  makeRotationFromQuaternion(q: Quaternion) {
    return this.compose(_zero, q, _one);
  }

  lookAt(eye: Vector3, target: Vector3, up: Vector3) {
    const te = this.value;

    _z.subVectors(eye, target);

    if (_z.lengthSq() === 0) {
      // eye and target are in the same position
      _z.z = 1;
    }

    _z.normalize();
    _x.crossVectors(up, _z);

    if (_x.lengthSq() === 0) {
      // up and z are parallel

      if (Math.abs(up.z) === 1) {
        _z.x += 0.0001;
      } else {
        _z.z += 0.0001;
      }

      _z.normalize();
      _x.crossVectors(up, _z);
    }

    _x.normalize();
    _y.crossVectors(_z, _x);

    te[0] = _x.x;
    te[4] = _y.x;
    te[8] = _z.x;
    te[1] = _x.y;
    te[5] = _y.y;
    te[9] = _z.y;
    te[2] = _x.z;
    te[6] = _y.z;
    te[10] = _z.z;

    return this;
  }

  multiplyMatrices(a: Matrix4, b: Matrix4) {
    const ae = a.value;
    const be = b.value;
    const te = this.value;

    const a11 = ae[0];
    const a12 = ae[4];
    const a13 = ae[8];
    const a14 = ae[12];
    const a21 = ae[1];
    const a22 = ae[5];
    const a23 = ae[9];
    const a24 = ae[13];
    const a31 = ae[2];
    const a32 = ae[6];
    const a33 = ae[10];
    const a34 = ae[14];
    const a41 = ae[3];
    const a42 = ae[7];
    const a43 = ae[11];
    const a44 = ae[15];

    const b11 = be[0];
    const b12 = be[4];
    const b13 = be[8];
    const b14 = be[12];
    const b21 = be[1];
    const b22 = be[5];
    const b23 = be[9];
    const b24 = be[13];
    const b31 = be[2];
    const b32 = be[6];
    const b33 = be[10];
    const b34 = be[14];
    const b41 = be[3];
    const b42 = be[7];
    const b43 = be[11];
    const b44 = be[15];

    te[0] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
    te[4] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
    te[8] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
    te[12] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;

    te[1] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
    te[5] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
    te[9] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
    te[13] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;

    te[2] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
    te[6] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
    te[10] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
    te[14] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;

    te[3] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
    te[7] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
    te[11] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
    te[15] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;

    return this;
  }

  multiply(m: Matrix4) {
    return this.multiplyMatrices(this, m);
  }

  premultiply(m: Matrix4) {
    return this.multiplyMatrices(m, this);
  }

  multiplyScalar(s: number) {
    this.m11 *= s;
    this.m12 *= s;
    this.m13 *= s;
    this.m14 *= s;
    this.m21 *= s;
    this.m22 *= s;
    this.m23 *= s;
    this.m24 *= s;
    this.m31 *= s;
    this.m32 *= s;
    this.m33 *= s;
    this.m34 *= s;
    return this;
  }

  /// 行列式
  determinant() {
    const te = this.value;

    const n11 = te[0];
    const n12 = te[4];
    const n13 = te[8];
    const n14 = te[12];
    const n21 = te[1];
    const n22 = te[5];
    const n23 = te[9];
    const n24 = te[13];
    const n31 = te[2];
    const n32 = te[6];
    const n33 = te[10];
    const n34 = te[14];
    const n41 = te[3];
    const n42 = te[7];
    const n43 = te[11];
    const n44 = te[15];

    //TODO: make this more efficient
    //( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

    return (
      n41 *
        (+n14 * n23 * n32 -
          n13 * n24 * n32 -
          n14 * n22 * n33 +
          n12 * n24 * n33 +
          n13 * n22 * n34 -
          n12 * n23 * n34) +
      n42 *
        (+n11 * n23 * n34 -
          n11 * n24 * n33 +
          n14 * n21 * n33 -
          n13 * n21 * n34 +
          n13 * n24 * n31 -
          n14 * n23 * n31) +
      n43 *
        (+n11 * n24 * n32 -
          n11 * n22 * n34 -
          n14 * n21 * n32 +
          n12 * n21 * n34 +
          n14 * n22 * n31 -
          n12 * n24 * n31) +
      n44 *
        (-n13 * n22 * n31 -
          n11 * n23 * n32 +
          n11 * n22 * n33 +
          n13 * n21 * n32 -
          n12 * n21 * n33 +
          n12 * n23 * n31)
    );
  }

  transpose() {
    const te = this.value;
    let tmp;

    tmp = te[1];
    te[1] = te[4];
    te[4] = tmp;
    tmp = te[2];
    te[2] = te[8];
    te[8] = tmp;
    tmp = te[6];
    te[6] = te[9];
    te[9] = tmp;

    tmp = te[3];
    te[3] = te[12];
    te[12] = tmp;
    tmp = te[7];
    te[7] = te[13];
    te[13] = tmp;
    tmp = te[11];
    te[11] = te[14];
    te[14] = tmp;

    return this;
  }

  setPosition(x: number, y: number, z: number): this;
  setPosition(x: Vector3): this;
  setPosition(x: Vector3 | number, y?: number, z?: number): this {
    const te = this.value;
    if (x instanceof Vector3) {
      te[12] = x.x;
      te[13] = x.y;
      te[14] = x.z;
    } else if (
      typeof x == "number" &&
      typeof y == "number" &&
      typeof z == "number"
    ) {
      te[12] = x;
      te[13] = y;
      te[14] = z;
    }
    return this;
  }

  inverse() {
    const te = this.value;
    const n11 = te[0];
    const n21 = te[1];
    const n31 = te[2];
    const n41 = te[3];
    const n12 = te[4];
    const n22 = te[5];
    const n32 = te[6];
    const n42 = te[7];
    const n13 = te[8];
    const n23 = te[9];
    const n33 = te[10];
    const n43 = te[11];
    const n14 = te[12];
    const n24 = te[13];
    const n34 = te[14];
    const n44 = te[15];
    const t11 =
      n23 * n34 * n42 -
      n24 * n33 * n42 +
      n24 * n32 * n43 -
      n22 * n34 * n43 -
      n23 * n32 * n44 +
      n22 * n33 * n44;
    const t12 =
      n14 * n33 * n42 -
      n13 * n34 * n42 -
      n14 * n32 * n43 +
      n12 * n34 * n43 +
      n13 * n32 * n44 -
      n12 * n33 * n44;
    const t13 =
      n13 * n24 * n42 -
      n14 * n23 * n42 +
      n14 * n22 * n43 -
      n12 * n24 * n43 -
      n13 * n22 * n44 +
      n12 * n23 * n44;
    const t14 =
      n14 * n23 * n32 -
      n13 * n24 * n32 -
      n14 * n22 * n33 +
      n12 * n24 * n33 +
      n13 * n22 * n34 -
      n12 * n23 * n34;

    const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

    if (det === 0) {
      return this.set(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
    }

    const detInv = 1 / det;

    te[0] = t11 * detInv;
    te[1] =
      (n24 * n33 * n41 -
        n23 * n34 * n41 -
        n24 * n31 * n43 +
        n21 * n34 * n43 +
        n23 * n31 * n44 -
        n21 * n33 * n44) *
      detInv;
    te[2] =
      (n22 * n34 * n41 -
        n24 * n32 * n41 +
        n24 * n31 * n42 -
        n21 * n34 * n42 -
        n22 * n31 * n44 +
        n21 * n32 * n44) *
      detInv;
    te[3] =
      (n23 * n32 * n41 -
        n22 * n33 * n41 -
        n23 * n31 * n42 +
        n21 * n33 * n42 +
        n22 * n31 * n43 -
        n21 * n32 * n43) *
      detInv;

    te[4] = t12 * detInv;
    te[5] =
      (n13 * n34 * n41 -
        n14 * n33 * n41 +
        n14 * n31 * n43 -
        n11 * n34 * n43 -
        n13 * n31 * n44 +
        n11 * n33 * n44) *
      detInv;
    te[6] =
      (n14 * n32 * n41 -
        n12 * n34 * n41 -
        n14 * n31 * n42 +
        n11 * n34 * n42 +
        n12 * n31 * n44 -
        n11 * n32 * n44) *
      detInv;
    te[7] =
      (n12 * n33 * n41 -
        n13 * n32 * n41 +
        n13 * n31 * n42 -
        n11 * n33 * n42 -
        n12 * n31 * n43 +
        n11 * n32 * n43) *
      detInv;

    te[8] = t13 * detInv;
    te[9] =
      (n14 * n23 * n41 -
        n13 * n24 * n41 -
        n14 * n21 * n43 +
        n11 * n24 * n43 +
        n13 * n21 * n44 -
        n11 * n23 * n44) *
      detInv;
    te[10] =
      (n12 * n24 * n41 -
        n14 * n22 * n41 +
        n14 * n21 * n42 -
        n11 * n24 * n42 -
        n12 * n21 * n44 +
        n11 * n22 * n44) *
      detInv;
    te[11] =
      (n13 * n22 * n41 -
        n12 * n23 * n41 -
        n13 * n21 * n42 +
        n11 * n23 * n42 +
        n12 * n21 * n43 -
        n11 * n22 * n43) *
      detInv;

    te[12] = t14 * detInv;
    te[13] =
      (n13 * n24 * n31 -
        n14 * n23 * n31 +
        n14 * n21 * n33 -
        n11 * n24 * n33 -
        n13 * n21 * n34 +
        n11 * n23 * n34) *
      detInv;
    te[14] =
      (n14 * n22 * n31 -
        n12 * n24 * n31 -
        n14 * n21 * n32 +
        n11 * n24 * n32 +
        n12 * n21 * n34 -
        n11 * n22 * n34) *
      detInv;
    te[15] =
      (n12 * n23 * n31 -
        n13 * n22 * n31 +
        n13 * n21 * n32 -
        n11 * n23 * n32 -
        n12 * n21 * n33 +
        n11 * n22 * n33) *
      detInv;
    return this;
  }

  getMaxScaleOnAxis() {
    const te = this.value;
    const scaleXSq = te[0] * te[0] + te[1] * te[1] + te[2] * te[2];
    const scaleYSq = te[4] * te[4] + te[5] * te[5] + te[6] * te[6];
    const scaleZSq = te[8] * te[8] + te[9] * te[9] + te[10] * te[10];
    return Math.sqrt(Math.max(scaleXSq, scaleYSq, scaleZSq));
  }

  makeTranslation(x: Vector3 | number, y?: number, z?: number) {
    if (x instanceof Vector3) {
      this.set(1, 0, 0, x.x, 0, 1, 0, x.y, 0, 0, 1, x.z, 0, 0, 0, 1);
    } else {
      this.set(1, 0, 0, x, 0, 1, 0, y, 0, 0, 1, z, 0, 0, 0, 1);
    }
    return this;
  }

  makeRotationX(theta: number) {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    this.set(1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0, 0, 0, 0, 1);
    return this;
  }

  makeRotationY(theta: number) {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    this.set(c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0, 0, 0, 0, 1);
    return this;
  }

  makeRotationZ(theta: number) {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    this.set(c, -s, 0, 0, s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
    return this;
  }

  makeRotationAxis(axis: Vector3, angle: number) {
    // Based on http://www.gamedev.net/reference/articles/article1199.asp

    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const t = 1 - c;
    const x = axis.x;
    const y = axis.y;
    const z = axis.z;
    const tx = t * x;
    const ty = t * y;

    this.set(
      tx * x + c,
      tx * y - s * z,
      tx * z + s * y,
      0,
      tx * y + s * z,
      ty * y + c,
      ty * z - s * x,
      0,
      tx * z - s * y,
      ty * z + s * x,
      t * z * z + c,
      0,
      0,
      0,
      0,
      1
    );

    return this;
  }

  makeScale(x: number, y: number, z: number) {
    this.set(x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1);
    return this;
  }

  makeShear(
    xy: number,
    xz: number,
    yx: number,
    yz: number,
    zx: number,
    zy: number
  ) {
    this.set(1, yx, zx, 0, xy, 1, zy, 0, xz, yz, 1, 0, 0, 0, 0, 1);
    return this;
  }

  makePerspective(
    left: number,
    right: number,
    top: number,
    bottom: number,
    near: number,
    far: number,
    coordinateSystem = WebGLCoordinateSystem
  ) {
    const te = this.value;
    const x = (2 * near) / (right - left);
    const y = (2 * near) / (top - bottom);

    const a = (right + left) / (right - left);
    const b = (top + bottom) / (top - bottom);

    let c, d;

    if (coordinateSystem === WebGLCoordinateSystem) {
      c = -(far + near) / (far - near);
      d = (-2 * far * near) / (far - near);
    } else if (coordinateSystem === WebGPUCoordinateSystem) {
      c = -far / (far - near);
      d = (-far * near) / (far - near);
    } else {
      throw new Error(
        "Matrix4.makePerspective(): Invalid coordinate system: " +
          coordinateSystem
      );
    }

    te[0] = x;
    te[4] = 0;
    te[8] = a;
    te[12] = 0;
    te[1] = 0;
    te[5] = y;
    te[9] = b;
    te[13] = 0;
    te[2] = 0;
    te[6] = 0;
    te[10] = c;
    te[14] = d;
    te[3] = 0;
    te[7] = 0;
    te[11] = -1;
    te[15] = 0;

    return this;
  }

  makeOrthographic(
    left: number,
    right: number,
    top: number,
    bottom: number,
    near: number,
    far: number,
    coordinateSystem = WebGLCoordinateSystem
  ) {
    const te = this.value;
    const w = 1.0 / (right - left);
    const h = 1.0 / (top - bottom);
    const p = 1.0 / (far - near);

    const x = (right + left) * w;
    const y = (top + bottom) * h;

    let z, zInv;

    if (coordinateSystem === WebGLCoordinateSystem) {
      z = (far + near) * p;
      zInv = -2 * p;
    } else if (coordinateSystem === WebGPUCoordinateSystem) {
      z = near * p;
      zInv = -1 * p;
    } else {
      throw new Error(
        "Matrix4.makeOrthographic(): Invalid coordinate system: " +
          coordinateSystem
      );
    }

    te[0] = 2 * w;
    te[4] = 0;
    te[8] = 0;
    te[12] = -x;
    te[1] = 0;
    te[5] = 2 * h;
    te[9] = 0;
    te[13] = -y;
    te[2] = 0;
    te[6] = 0;
    te[10] = zInv;
    te[14] = -z;
    te[3] = 0;
    te[7] = 0;
    te[11] = 0;
    te[15] = 1;

    return this;
  }

  compose(position: Vector3, quaternion: Quaternion, scale: Vector3) {
    const te = this.value;

    const x = quaternion.x;
    const y = quaternion.y;
    const z = quaternion.z;
    const w = quaternion.w;
    const x2 = x + x;
    const y2 = y + y;
    const z2 = z + z;
    const xx = x * x2;
    const xy = x * y2;
    const xz = x * z2;
    const yy = y * y2;
    const yz = y * z2;
    const zz = z * z2;
    const wx = w * x2;
    const wy = w * y2;
    const wz = w * z2;

    const sx = scale.x;
    const sy = scale.y;
    const sz = scale.z;

    te[0] = (1 - (yy + zz)) * sx;
    te[1] = (xy + wz) * sx;
    te[2] = (xz - wy) * sx;
    te[3] = 0;

    te[4] = (xy - wz) * sy;
    te[5] = (1 - (xx + zz)) * sy;
    te[6] = (yz + wx) * sy;
    te[7] = 0;

    te[8] = (xz + wy) * sz;
    te[9] = (yz - wx) * sz;
    te[10] = (1 - (xx + yy)) * sz;
    te[11] = 0;

    te[12] = position.x;
    te[13] = position.y;
    te[14] = position.z;
    te[15] = 1;

    return this;
  }

  decompose(position: Vector3, quaternion: Quaternion, scale: Vector3) {
    const te = this.value;

    let sx = _v3.set(te[0], te[1], te[2]).length();
    const sy = _v3.set(te[4], te[5], te[6]).length();
    const sz = _v3.set(te[8], te[9], te[10]).length();

    // if determine is negative, we need to invert one scale
    const det = this.determinant();
    if (det < 0) sx = -sx;

    position.x = te[12];
    position.y = te[13];
    position.z = te[14];

    // scale the rotation part
    _m4.copy(this);

    const invSX = 1 / sx;
    const invSY = 1 / sy;
    const invSZ = 1 / sz;

    _m4.value[0] *= invSX;
    _m4.value[1] *= invSX;
    _m4.value[2] *= invSX;

    _m4.value[4] *= invSY;
    _m4.value[5] *= invSY;
    _m4.value[6] *= invSY;

    _m4.value[8] *= invSZ;
    _m4.value[9] *= invSZ;
    _m4.value[10] *= invSZ;

    quaternion.setFromRotationMatrix4(_m4);

    scale.x = sx;
    scale.y = sy;
    scale.z = sz;

    return this;
  }

  equals(matrix: Matrix4) {
    for (let i = 0; i < 16; i++) {
      if (this.value[i] !== matrix.value[i]) {
        return false;
      }
    }
    return true;
  }

  translate(x = 0, y = 0, z = 0) {
    return this.multiply(
      new Matrix4([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1])
    );
  }

  scale(x = 1, y = 1, z = 1) {
    return this.multiply(
      new Matrix4([x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1])
    );
  }

  scaleVector3(v: Vector3) {
    const te = this.value;
    const x = v.x;
    const y = v.y;
    const z = v.z;

    te[0] *= x;
    te[4] *= y;
    te[8] *= z;
    te[1] *= x;
    te[5] *= y;
    te[9] *= z;
    te[2] *= x;
    te[6] *= y;
    te[10] *= z;
    te[3] *= x;
    te[7] *= y;
    te[11] *= z;

    return this;
  }

  // rotate(degX = 0, degY = 0, degZ = 0) {
  //   if (degZ) {
  //     this.is3dSpecial = true;
  //   }
  //   const x = toRadian(degX);
  //   const y = toRadian(degY);
  //   const z = toRadian(degZ);
  //   const a = Math.cos(x);
  //   const b = Math.sin(x);
  //   const c = Math.cos(y);
  //   const d = Math.sin(y);
  //   const e = Math.cos(z);
  //   const f = Math.sin(z);
  //   const ae = a * e;
  //   const af = a * f;
  //   const be = b * e;
  //   const bf = b * f;
  //   this.m11 = c * e;
  //   this.m21 = -c * f;
  //   this.m31 = d;
  //   this.m12 = af + be * d;
  //   this.m22 = ae - bf * d;
  //   this.m32 = -b * c;
  //   this.m13 = bf - ae * d;
  //   this.m23 = be + af * d;
  //   this.m33 = a * c;
  //   return this;
  // }

  rotateX(degree: number) {
    const a = toRadian(degree);
    const c = Math.cos(a);
    const s = Math.sin(a);
    return this.multiply(
      // new Matrix4([1, 0, 0, 0, 0, c, -s, 0, 0, s, c, 0, 0, 0, 0, 1])
      new Matrix4([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1])
    );
  }

  rotateY(degree: number) {
    const a = toRadian(degree);
    const c = Math.cos(a);
    const s = Math.sin(a);
    return this.multiply(
      // new Matrix4([c, 0, s, 0, 0, 1, 0, 0, -s, 0, c, 0, 0, 0, 0, 1])
      new Matrix4([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1])
    );
  }

  /// this.multiply([c, s, -s, c, 0, 0]);
  rotateZ(degree: number) {
    this.is3dSpecial = true;
    const a = toRadian(degree);
    const c = Math.cos(a);
    const s = Math.sin(a);
    return this.multiply(
      // new Matrix4([c, -s, 0, 0, s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])
      new Matrix4([c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])
    );
  }

  skew(degX = 0, degY = 0, degZ = 0) {
    return this.multiply(
      new Matrix4([
        1,
        0,
        0,
        toRadian(degX),
        0,
        1,
        0,
        toRadian(degY),
        0,
        0,
        1,
        toRadian(degZ),
        0,
        0,
        0,
        1
      ])
    );
  }

  perspective(perspective: number) {
    return this.multiply(
      new Matrix4([
        1,
        0,
        0,
        0,
        0,
        1,
        0,
        0,
        0,
        0,
        1,
        perspective ? -(1 / perspective) : 0,
        0,
        0,
        0,
        1
      ])
    );
  }

  // based on http://code.metager.de/source/xref/mozilla/B2G/gecko/gfx/thebes/gfx3DMatrix.cpp#651
  unproject(point: { x: number; y: number }) {
    let x = point.x * this.m11 + point.y * this.m21 + this.m41;
    let y = point.x * this.m12 + point.y * this.m22 + this.m42;
    let z = point.x * this.m13 + point.y * this.m23 + this.m43;
    let w = point.x * this.m14 + point.y * this.m24 + this.m44;

    let qx = x + this.m31;
    let qy = y + this.m32;
    let qz = z + this.m33;
    let qw = w + this.m34;

    if (w == 0) w = 0.0001;
    x /= w;
    y /= w;
    z /= w;

    if (qw == 0) qw = 0.0001;
    qx /= qw;
    qy /= qw;
    qz /= qw;

    // TODO: ....
    const wz = qz - z;
    if (wz == 0) {
      return { x, y, z, mass: w };
    }

    const t = -z / wz;
    x += t * (qx - x);
    y += t * (qy - y);

    return { x, y, z, mass: w };
  }

  // from homegeneous (euclid) to cartesian FLATTENED!!!! like a projection
  project(point: Point3D) {
    const z = point.z;
    let x = point.x * this.m11 + point.y * this.m21 + z * this.m31 + this.m41;
    let y = point.x * this.m12 + point.y * this.m22 + z * this.m32 + this.m42;
    let w = point.x * this.m14 + point.y * this.m24 + z * this.m34 + this.m44;

    if (w == 0) w = 0.0001;
    x /= w;
    y /= w;

    // lets call it "hasenfuss"
    // look at the developer tools of firefox and chrome ->
    // ff and chrome do it wrong: the highlighted bounding box failed to be correct
    // and getBoundingClientRect also

    // behind the "camera" (z > 0)
    if (w < 0) {
      x -= this.m41;
      y -= this.m42;
      x *= 1 / w;
      y *= 1 / w;
      x += this.m41;
      y += this.m42;
    }
    return { x, y, z, mass: w };
  }

  bounds(
    x: number,
    y: number,
    width: number,
    height: number
  ): { x: number; y: number; width: number; height: number } {
    const ptA = new Point3D({ x, y });
    const ptB = new Point3D({ x: x + width, y });
    const ptC = new Point3D({ x: x + width, y: y + height });
    const ptD = new Point3D({ x, y: y + height });

    ({ x: ptA.x, y: ptA.y, z: ptA.z, mass: ptA.mass } = this.project(ptA));
    ({ x: ptB.x, y: ptB.y, z: ptB.z, mass: ptB.mass } = this.project(ptB));
    ({ x: ptC.x, y: ptC.y, z: ptC.z, mass: ptC.mass } = this.project(ptC));
    ({ x: ptD.x, y: ptD.y, z: ptD.z, mass: ptD.mass } = this.project(ptD));

    const x2 = Math.min(ptA.x, ptB.x, ptC.x, ptD.x);
    const y2 = Math.min(ptA.y, ptB.y, ptC.y, ptD.y);
    const width2 = Math.max(ptA.x, ptB.x, ptC.x, ptD.x) - x2;
    const height2 = Math.max(ptA.y, ptB.y, ptC.y, ptD.y) - y2;

    return { x: x2, y: y2, width: width2, height: height2 };
  }
}

export class Matrix {
  /// 按列顺序
  value: TMatrixValue;
  private _m13 = 0;
  private _m23 = 0;
  private _m33 = 1;

  matrix4: Matrix4;

  constructor(value: TMatrixValue = DEFAULT_MATRIX_VALUE) {
    this.value = Array.from(value) as TMatrixValue;
    this.matrix4 = this.toMatrix4();
  }

  get m11() {
    return this.value[0];
  }
  get m12() {
    return this.value[1];
  }
  get m13() {
    return this._m13;
  }
  get m21() {
    return this.value[2];
  }
  get m22() {
    return this.value[3];
  }
  get m23() {
    return this._m23;
  }
  get m31() {
    return this.value[4];
  }
  get m32() {
    return this.value[5];
  }
  get m33() {
    return this._m33;
  }

  set m11(v: number) {
    this.value[0] = v;
  }
  set m12(v: number) {
    this.value[1] = v;
  }
  set m13(v: number) {
    this._m13 = v;
  }
  set m21(v: number) {
    this.value[2] = v;
  }
  set m22(v: number) {
    this.value[3] = v;
  }
  set m23(v: number) {
    this._m23 = v;
  }
  set m31(v: number) {
    this.value[4] = v;
  }
  set m32(v: number) {
    this.value[5] = v;
  }
  set m33(v: number) {
    this._m33 = v;
  }

  // matrix(1, 0, 0, 1, -50, -50)
  static fromString(s: string) {
    const m = new Matrix();
    const results = regMatchAll("number", s);
    if (results.length === 6) {
      results.forEach((result, index) => {
        if (result.fullMatch) {
          m.value[index] = parseFloat(result.fullMatch);
        }
      });
    }
    return m;
  }

  toMatrix4(matrix4 = this.matrix4) {
    const value: TMatrix4Value = [
      this.m11,
      this.m12,
      0,
      this.m13,
      this.m21,
      this.m22,
      0,
      this.m23,
      0,
      0,
      1,
      0,
      this.m31,
      this.m32,
      0,
      this.m33
    ];
    if (matrix4) {
      matrix4.value = value;
      return matrix4;
    }
    return new Matrix4(value);
  }

  identity() {
    this.value = Array.from(DEFAULT_MATRIX_VALUE) as TMatrixValue;
    this.m13 = 0;
    this.m23 = 0;
    this.m33 = 1;
    this.matrix4 = this.toMatrix4();
    return this;
  }

  get isIdentity(): boolean {
    return (
      this.m11 == 1 &&
      this.m12 == 0 &&
      this.m13 == 0 &&
      this.m21 == 0 &&
      this.m22 == 1 &&
      this.m23 == 0 &&
      this.m31 == 0 &&
      this.m32 == 0 &&
      this.m33 == 1
    );
  }

  multiplyScalar(s: number) {
    this.m11 *= s;
    this.m12 *= s;
    this.m13 *= s;
    this.m21 *= s;
    this.m22 *= s;
    this.m23 *= s;
    this.m31 *= s;
    this.m32 *= s;
    this.m33 *= s;
    return this;
  }

  normalize() {
    const m33 = this.m33 || 0.0001;
    return this.multiplyScalar(1 / m33);
  }

  transformPoint(point: { x: number; y: number; z?: number }): {
    x: number;
    y: number;
    z: number;
  } {
    const z = point.z || 1;
    return {
      x: point.x * this.m11 + point.y * this.m21 + z * this.m31,
      y: point.x * this.m12 + point.y * this.m22 + z * this.m32,
      z: point.x * this.m13 + point.y * this.m23 + z * this.m33
    };
  }

  clone(): Matrix {
    return new Matrix(this.value);
  }

  multiply(matrix: Matrix | TMatrixValue) {
    const a = this.clone();
    const b = matrix instanceof Matrix ? matrix : new Matrix(matrix);
    this.m11 = b.m11 * a.m11 + b.m12 * a.m21 + b.m13 * a.m31;
    this.m12 = b.m11 * a.m12 + b.m12 * a.m22 + b.m13 * a.m32;
    this.m13 = b.m11 * a.m13 + b.m12 * a.m23 + b.m13 * a.m33;
    this.m21 = b.m21 * a.m11 + b.m22 * a.m21 + b.m23 * a.m31;
    this.m22 = b.m21 * a.m12 + b.m22 * a.m22 + b.m23 * a.m32;
    this.m23 = b.m21 * a.m13 + b.m22 * a.m23 + b.m23 * a.m33;
    this.m31 = b.m31 * a.m11 + b.m32 * a.m21 + b.m33 * a.m31;
    this.m32 = b.m31 * a.m12 + b.m32 * a.m22 + b.m33 * a.m32;
    this.m33 = b.m31 * a.m13 + b.m32 * a.m23 + b.m33 * a.m33;
    return this;
  }

  append(matrix: Matrix) {
    return this.multiply(matrix);
  }

  prepend(matrix: Matrix) {
    matrix.multiply(this);
    return this;
  }

  inverse() {
    // const m11 = this.m11;
    // const m12 = this.m12;
    // const m21 = this.m21;
    // const m22 = this.m22;
    // const m31 = this.m31;
    // const m32 = this.m32;
    // const n = m11 * m22 - m12 * m21;
    // this.m11 = m22 / n;
    // this.m12 = -m12 / n;
    // this.m21 = -m21 / n;
    // this.m22 = m11 / n;
    // this.m31 = (m21 * m32 - m22 * m31) / n;
    // this.m32 = -(m11 * m32 - m12 * m31) / n;
    // this.m13 = 0;
    // this.m23 = 0;
    // this.m33 = 1;
    const a00 = this.m11;
    const a01 = this.m12;
    const a02 = this.m13;
    const a10 = this.m21;
    const a11 = this.m22;
    const a12 = this.m23;
    const a20 = this.m31;
    const a21 = this.m32;
    const a22 = this.m33;

    const b01 = a22 * a11 - a12 * a21;
    const b11 = -a22 * a10 + a12 * a20;
    const b21 = a21 * a10 - a11 * a20;

    // Calculate the determinant
    let det = a00 * b01 + a01 * b11 + a02 * b21;

    if (!det) {
      return null;
    }
    det = 1.0 / det;

    this.m11 = b01 * det;
    this.m12 = (-a22 * a01 + a02 * a21) * det;
    this.m13 = (a12 * a01 - a02 * a11) * det;
    this.m21 = b11 * det;
    this.m22 = (a22 * a00 - a02 * a20) * det;
    this.m23 = (-a12 * a00 + a02 * a10) * det;
    this.m31 = b21 * det;
    this.m32 = (-a21 * a00 + a01 * a20) * det;
    this.m33 = (a11 * a00 - a01 * a10) * det;
    return this;
  }

  adjugate(): Matrix {
    const matrix = new Matrix();
    matrix.m11 = this.m22 * this.m33 - this.m32 * this.m23;
    matrix.m12 = this.m32 * this.m13 - this.m12 * this.m33;
    matrix.m13 = this.m12 * this.m23 - this.m22 * this.m13;
    matrix.m21 = this.m31 * this.m23 - this.m21 * this.m33;
    matrix.m22 = this.m11 * this.m33 - this.m31 * this.m13;
    matrix.m23 = this.m21 * this.m13 - this.m11 * this.m23;
    matrix.m31 = this.m21 * this.m32 - this.m31 * this.m22;
    matrix.m32 = this.m31 * this.m12 - this.m11 * this.m32;
    matrix.m33 = this.m11 * this.m22 - this.m21 * this.m12;
    return matrix;
  }

  split() {
    let degree = toDegree(Math.atan2(this.m12, this.m11));
    if (this.m11 < 0 && this.m22 >= 0) {
      degree += 180;
    }
    return {
      x: this.m31,
      y: this.m32,
      degree,
      scaleX: Math.sqrt(this.m11 * this.m11 + this.m12 * this.m12),
      scaleY: Math.sqrt(this.m21 * this.m21 + this.m22 * this.m22),
      skewX: toDegree(Math.atan2(-this.m21, this.m22)),
      skewY: toDegree(Math.atan2(this.m12, this.m11))
    };
  }

  getGroupCoords(group: Matrix) {
    // possible to replace with node * group.inverse()
    const M = this.value[0],
      N = this.value[1],
      O = this.value[2],
      P = this.value[3],
      R = this.value[4],
      S = this.value[5],
      A = group.value[0],
      B = group.value[1],
      C = group.value[2],
      D = group.value[3],
      E = group.value[4],
      F = group.value[5],
      AD = A * D,
      BC = B * C,
      G = (C * N - M * D) / (BC - AD),
      H = (A * N - M * B) / (AD - BC),
      I = (C * P - O * D) / (BC - AD),
      J = (A * P - O * B) / (AD - BC),
      K = (C * (S - F) + D * (E - R)) / (BC - AD),
      L = (A * (S - F) + B * (E - R)) / (AD - BC);
    const matrix = [G, H, I, J, K, L];
    return new Matrix(matrix as TMatrixValue);
  }

  toString(fractionalDigits = 6) {
    if (this.matrix4.is2D()) {
      return `matrix(${this.value
        .map((v) => maxDotNumber(v, fractionalDigits))
        .join(",")})`;
    } else {
      return this.matrix4.toString();
    }
  }

  // toFilter() {
  //   return (
  //     "progid:DXImageTransform.Microsoft.Matrix(M11=" +
  //     this.value[0] +
  //     ", M12=" +
  //     this.value[2] +
  //     ", M21=" +
  //     this.value[1] +
  //     ", M22=" +
  //     this.value[3] +
  //     ", Dx=" +
  //     this.value[4] +
  //     ", Dy=" +
  //     this.value[5] +
  //     ", sizingmethod='auto expand')"
  //   );
  // }

  // 相对值
  translate(x: number, y: number) {
    this.multiply([1, 0, 0, 1, x, y]);
    this.toMatrix4();
    return this;
  }
  // 绝对值
  translateTo(x: number, y: number) {
    return this.translate(x - this.m31, y - this.m32);
  }
  translateX(x: number) {
    return this.translate(x, 0);
  }
  translateXTo(x: number) {
    return this.translateTo(x, this.m32);
  }
  translateY(y: number) {
    return this.translate(0, y);
  }
  translateYTo(y: number) {
    return this.translateTo(this.m31, y);
  }
  translateZ(z: number) {
    this.matrix4.translate(0, 0, z);
    this.matrix4.toMatrix(this);
    return this;
  }
  translateZTo(z: number) {
    this.translateZ(z - this.matrix4.m43);
    return this;
  }
  translate3d(x: number, y: number, z: number) {
    this.matrix4.translate(x, y, z);
    this.matrix4.toMatrix(this);
    return this;
  }
  translate3dTo(x: number, y: number, z: number) {
    this.translate3d(
      x - this.matrix4.m41,
      y - this.matrix4.m42,
      z - this.matrix4.m43
    );
    return this;
  }

  rotate(deg: number, centerX = 0, centerY = 0) {
    const rad = toRadian(deg);
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    this.translate(centerX, centerY);
    this.multiply([c, s, -s, c, 0, 0]);
    this.translate(-centerX, -centerY);
    this.toMatrix4();
    return this;
  }
  rotateTo(targetDeg: number, centerX = 0, centerY = 0) {
    const { degree } = this.split();
    this.rotate(targetDeg - degree, centerX, centerY);
    return this;
  }
  rotateX(deg: number) {
    this.matrix4.rotateX(deg);
    this.matrix4.toMatrix(this);
    return this;
  }
  rotateXTo(targetDeg: number) {
    const { degreeX } = this.matrix4.split();
    this.rotateX(targetDeg - degreeX);
    return this;
  }
  rotateY(deg: number) {
    this.matrix4.rotateY(deg);
    this.matrix4.toMatrix(this);
    return this;
  }
  rotateYTo(targetDeg: number) {
    const { degreeY } = this.matrix4.split();
    this.rotateY(targetDeg - degreeY);
    return this;
  }

  rotateZ(deg: number) {
    this.matrix4.rotateZ(deg);
    // this.matrix4.rotate(0, 0, deg);
    this.matrix4.toMatrix(this);
    return this;
  }
  rotateZTo(targetDeg: number) {
    const { degreeZ } = this.matrix4.split();
    this.rotateZ(targetDeg - degreeZ);
    return this;
  }
  rotate3d(x: number, y: number, z: number) {
    // this.matrix4.rotate(x, y, z);
    this.matrix4.rotateX(x).rotateY(y).rotateZ(z);
    this.matrix4.toMatrix(this);
    return this;
  }
  rotate3dTo(targetX: number, targetY: number, targetZ: number) {
    const { degreeX, degreeY, degreeZ } = this.matrix4.split();
    this.rotate3d(targetX - degreeX, targetY - degreeY, targetZ - degreeZ);
    return this;
  }

  scale(sx: number, sy = sx, centerX = 0, centerY = 0) {
    this.translate(centerX, centerY);
    this.multiply([sx, 0, 0, sy, 0, 0]);
    this.translate(-centerX, -centerY);
    this.toMatrix4();
    return this;
  }
  scaleTo(targetSx: number, targetSy = targetSx, centerX = 0, centerY = 0) {
    return this.scale(
      targetSx / this.m11,
      targetSy / this.m22,
      centerX,
      centerY
    );
  }
  scaleX(sx: number) {
    return this.scale(sx, 1);
  }
  scaleXTo(sx: number) {
    return this.scale(sx / this.m11, 1);
  }
  scaleY(sy: number) {
    return this.scale(1, sy);
  }
  scaleYTo(sy: number) {
    return this.scale(1, sy / this.m22);
  }
  scaleZ(sz: number) {
    this.matrix4.scale(1, 1, sz);
    this.matrix4.toMatrix(this);
    return this;
  }
  scaleZTo(sz: number) {
    const { scalarZ } = this.matrix4.split();
    this.scaleZ(sz / scalarZ);
    return this;
  }
  scale3d(sx: number, sy = sx, sz = sx) {
    this.matrix4.scale(sx, sy, sz);
    this.matrix4.toMatrix(this);
    return this;
  }
  scale3dTo(sx: number, sy = sx, sz = sx) {
    const { scalarX, scalarY, scalarZ } = this.matrix4.split();
    this.scale3d(sx / scalarX, sy / scalarY, sz / scalarZ);
    return this;
  }

  skew(degX: number, degY = degX) {
    // const tx = Math.tan(toRadian(degX));
    // const ty = Math.tan(toRadian(degY));
    // this.multiply([1, ty, tx, 1, 0, 0]);
    this.multiply([
      Math.cos(toRadian(degY)),
      Math.sin(toRadian(degY)),
      -Math.sin(toRadian(degX)),
      Math.cos(toRadian(degX)),
      0,
      0
    ]);
    this.toMatrix4();
    return this;
  }
  skewTo(degX: number, degY = degX) {
    const { skewX, skewY } = this.split();
    return this.skew(degX - skewX, degY - skewY);
  }
  skewX(deg: number) {
    const { skewY } = this.split();
    return this.skew(deg, skewY);
  }
  skewXTo(deg: number) {
    const { skewY } = this.split();
    return this.skewTo(deg, skewY);
  }
  skewY(deg: number) {
    const { skewX } = this.split();
    return this.skew(skewX, deg);
  }
  skewYTo(deg: number) {
    const { skewX } = this.split();
    return this.skewTo(skewX, deg);
  }
  skewZ(deg: number) {
    /// UNDO
    return this;
  }
  skewZTo(deg: number) {
    /// UNDO
    return this;
  }
  skew3d(degx: number, degy: number, degz: number) {
    /// UNDO
    return this;
  }
  skew3dTo(degx: number, degy: number, degz: number) {
    /// UNDO
    return this;
  }

  perspective(perspective: number) {
    this.matrix4.perspective(perspective);
    this.matrix4.toMatrix(this);
    return this;
  }
}

export default Matrix;

const _zero = new Vector3(0, 0, 0);
const _one = new Vector3(1, 1, 1);
const _x = new Vector3();
const _y = new Vector3();
const _z = new Vector3();
const _v3 = new Vector3();
const _m3 = new Matrix3();
const _m4 = new Matrix4();

// 矩阵不满足交换律，所以顺序很重要
// matrix(0.707106781,0.707106781,-0.707106781,0.707106781,-0.7071067810000002,34.648232269)
// CSS transform: parseTransformToMatrix('rotate(45deg) translate(24px,25px)')
// SVG Path d : parseTransformToMatrix('r 45 t 24,25')
// SVG transform : parseTransformToMatrix('rotate(45) translate(24 25)')
// translate(-50%, -50%) 暂不支持%
export function parseTransformToMatrix(t: string) {
  const matrix = new Matrix();
  t.replace(
    // /([a-z]+)\(?([^)a-z]+)\)?/gi,
    /([a-z0-9]+)\s*\(?\s*((-?[0-9]*\.?[0-9]+(?:e[-+]?\d+)*[a-z%]*\s*,?\s*)+)\)?/gi,
    function (all, command, matchString) {
      if (matchString.indexOf(",") >= 0) {
        matchString = matchString.split(/\s*,\s*/);
      } else {
        matchString = matchString.split(/\s+/);
      }
      const values: number[] = [];
      for (let i = 0, count = matchString.length; i < count; i++) {
        if (matchString[i].trim()) {
          values.push(parseFloat("" + matchString[i]));
        }
      }
      switch (command.toLowerCase()) {
        case "t":
        case "translate":
          matrix.translate(values[0], values[1]);
          break;
        case "translatex":
          matrix.translateX(values[0]);
          break;
        case "translatey":
          matrix.translateY(values[0]);
          break;
        case "translatez":
          matrix.translateZ(values[0]);
          break;
        case "translate3d":
          matrix.translate3d(values[0], values[1], values[2]);
          break;
        case "r":
        case "rotate":
          matrix.rotate(values[0], values[1] ?? 0, values[2] ?? 0);
          break;
        case "rotatex":
          matrix.rotateX(values[0]);
          break;
        case "rotatey":
          matrix.rotateY(values[0]);
          break;
        case "rotatez":
          matrix.rotateZ(values[0]);
          break;
        case "rotate3d":
          matrix.rotate3d(values[0], values[1], values[2]);
          break;
        case "s":
        case "scale":
          matrix.scale(values[0], values[1] ?? values[0]);
          break;
        case "scalex":
          matrix.scaleX(values[0]);
          break;
        case "scaley":
          matrix.scaleY(values[0]);
          break;
        case "scalez":
          matrix.scaleZ(values[0]);
          break;
        case "scale3d":
          matrix.scale3d(values[0], values[1], values[2]);
          break;
        case "skew":
          matrix.skew(values[0], values[1] ?? 0);
          break;
        case "skewx":
          matrix.skewX(values[0]);
          break;
        case "skewy":
          matrix.skewY(values[0]);
          break;
        case "skewz":
          matrix.skewZ(values[0]);
          break;
        case "skew3d":
          matrix.skew3d(values[0], values[1], values[2]);
          break;
        case "m":
        case "matrix":
          matrix.multiply([
            values[0],
            values[1],
            values[2],
            values[3],
            values[4],
            values[5]
          ]);
          matrix.toMatrix4();
          break;
        case "matrix3d":
          matrix.matrix4.multiply(new Matrix4(values as TMatrix4Value));
          matrix.matrix4.toMatrix(matrix);
          break;
      }
      return "";
    }
  );
  return matrix;
}

export function rotatePoint(
  radian: number,
  pointX: number,
  pointY: number
): { x: number; y: number } {
  const m = new Matrix();
  m.rotate(toDegree(radian));
  return m.transformPoint({ x: pointX, y: pointY });
}

// see https://developer.mozilla.org/en-US/docs/Web/API/DOMMatrix/DOMMatrix
export function getDOMMatrix(options?: {
  originMatrix?: string | number[];
  x?: number;
  y?: number;
  degree?: number;
  scalarX?: number;
  scalarY?: number;
}): DOMMatrix {
  const x = typeof options?.x == "number" ? options?.x : 0;
  const y = typeof options?.y == "number" ? options?.y : 0;
  const degree = typeof options?.degree == "number" ? options?.degree : 0;
  const scalarX = typeof options?.scalarX == "number" ? options?.scalarX : 1;
  const scalarY = typeof options?.scalarY == "number" ? options?.scalarY : 1;
  const radian = toRadian(degree);
  const c = Math.cos(radian);
  const s = Math.sin(radian);
  const matrix = new DOMMatrix([
    c * scalarX,
    s * scalarX,
    -s * scalarY,
    c * scalarY,
    x,
    y
  ]);
  matrix.multiplySelf(new DOMMatrix(options?.originMatrix || ""));
  return matrix;
}

// ctx.beginPath();
// ctx.fill(pathd)
export function transformPath2D(
  pathString: string,
  options?: {
    originMatrix?: string | number[];
    x?: number;
    y?: number;
    degree?: number;
    scalarX?: number;
    scalarY?: number;
  }
): Path2D {
  // const x = typeof options?.x == "number" ? options?.x : 0;
  // const y = typeof options?.y == "number" ? options?.y : 0;
  // const degree = typeof options?.degree == "number" ? options?.degree : 0;
  // const scalarX = typeof options?.scalarX == "number" ? options?.scalarX : 1;
  // const scalarY = typeof options?.scalarY == "number" ? options?.scalarY : 1;
  // const path2d = new Path2D(pathString);
  // const t1 = new Path2D();
  // t1.addPath(path2d, new DOMMatrix(options?.originMatrix || ""));
  // t1.addPath(path2d);
  // const t2 = new Path2D();
  // const radian = toRadian(degree);
  // const c = Math.cos(radian);
  // const s = Math.sin(radian);
  // const matrix = new DOMMatrix([
  //   c * scalarX,
  //   s * scalarX,
  //   -s * scalarY,
  //   c * scalarY,
  //   x,
  //   y
  // ]);
  // t2.addPath(t1, matrix);
  // return t2;
  const path2d = new Path2D(pathString);
  const t = new Path2D();
  t.addPath(path2d, getDOMMatrix(options));
  return t;
}
